/**
 * The UI's internal message bus.
 *
 * Parts of the interface talk through topics rather than by holding references
 * to each other: the transport bar does not know the grid exists, the grid does
 * not know about plugins. Anything can say "the library changed" or "play this",
 * and anything interested listens. Engine events arrive here too (see
 * engineEvents.ts), under the same topic names the engine uses, so a plugin
 * listens to `library.changed` once whether the change happened in this window
 * or behind it.
 *
 * Patterns: an exact topic, `prefix.*` for a prefix and everything below it, or
 * `*` for everything. Handlers run synchronously; one that throws is logged and
 * skipped so it cannot stop the others.
 */

export interface BusEvent<T = unknown> {
  topic: string;
  data: T;
  /** "ui" for events raised in this window, "engine" for ones from the engine, or a plugin id. */
  source: string;
}

type Handler = (event: BusEvent) => void;

export function topicMatches(pattern: string, topic: string): boolean {
  if (pattern === "*" || pattern === topic) return true;
  if (pattern.endsWith(".*")) {
    const prefix = pattern.slice(0, -2);
    return topic === prefix || topic.startsWith(`${prefix}.`);
  }
  return false;
}

export class Bus {
  private handlers: { pattern: string; fn: Handler; owner: string }[] = [];

  on<T = unknown>(pattern: string, fn: (event: BusEvent<T>) => void, owner = "ui"): () => void {
    const entry = { pattern, fn: fn as Handler, owner };
    this.handlers.push(entry);
    return () => {
      this.handlers = this.handlers.filter((h) => h !== entry);
    };
  }

  emit<T = unknown>(topic: string, data?: T, source = "ui"): void {
    const event: BusEvent = { topic, data: data ?? null, source };
    for (const h of [...this.handlers]) {
      if (!topicMatches(h.pattern, topic)) continue;
      try {
        h.fn(event);
      } catch (err) {
        console.error(`[nova] handler from ${h.owner} failed on ${topic}`, err);
      }
    }
  }

  /** Drop every handler a plugin registered. */
  offOwner(owner: string): void {
    this.handlers = this.handlers.filter((h) => h.owner !== owner);
  }
}

/** The one bus for this window. */
export const bus = new Bus();
